import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, User, Mail, Phone, Building, GraduationCap } from 'lucide-react';
import { clearFormDraft, readFormDraft, writeFormDraft } from '@/lib/formDraftStorage';

interface MissingInfoFormProps {
  missingFields: string[];
  onProvideInfo: (info: Record<string, string>) => void;
  onContinue: () => void;
  isLoading?: boolean;
}

export function MissingInfoForm({ missingFields, onProvideInfo, onContinue, isLoading }: MissingInfoFormProps) {
  const [formData, setFormData] = useState<Record<string, string>>(() => (
    readFormDraft('missingInfo', {})
  ));

  useEffect(() => {
    writeFormDraft('missingInfo', formData);
  }, [formData]);

  const fieldConfigs = {
    fullName: {
      label: 'Full Name',
      icon: User,
      placeholder: 'Enter your full name',
      type: 'text'
    },
    email: {
      label: 'Email Address',
      icon: Mail,
      placeholder: 'your.email@example.com',
      type: 'email'
    },
    phone: {
      label: 'Phone Number',
      icon: Phone,
      placeholder: '+1 (555) 123-4567',
      type: 'tel'
    },
    workExperience: {
      label: 'Work Experience',
      icon: Building,
      placeholder: 'Company, Job Title, Duration, Key achievements',
      type: 'textarea'
    },
    education: {
      label: 'Education Details',
      icon: GraduationCap,
      placeholder: 'School, Degree, Graduation Year',
      type: 'textarea'
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProvideInfo = () => {
    const hasRequiredData = missingFields.some(field => {
      const value = formData[field];
      if (!value) return false;
      
      if (field === 'email') {
        return value.includes('@') && value.length > 5;
      }
      if (field === 'phone') {
        return value.replace(/\D/g, '').length >= 10;
      }
      if (field === 'fullName') {
        return value.trim().length > 2;
      }
      return value.trim().length > 5;
    });

    if (hasRequiredData) {
      clearFormDraft('missingInfo');
      onProvideInfo(formData);
    }
  };

  const handleContinue = () => {
    clearFormDraft('missingInfo');
    onContinue();
  };

  const getIcon = (IconComponent: any) => (
    <IconComponent className="w-4 h-4 text-muted-foreground" />
  );

  return (
    <Card className="w-full max-w-2xl mx-auto p-6 space-y-6 bg-card/50 backdrop-blur-sm border-border/50">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">Provide Missing Details</h3>
          <Badge variant="secondary" className="text-xs">
            {missingFields.length} field{missingFields.length > 1 ? 's' : ''} needed
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          You can provide these details now or continue with the optimization process using what we have.
        </p>
      </div>

      <div className="space-y-4">
        {missingFields.map(field => {
          const config = fieldConfigs[field as keyof typeof fieldConfigs];
          if (!config) return null;

          const IconComponent = config.icon;
          
          return (
            <div key={field} className="space-y-2">
              <Label htmlFor={field} className="flex items-center gap-2 text-sm font-medium">
                {getIcon(IconComponent)}
                {config.label}
              </Label>
              {config.type === 'textarea' ? (
                <Textarea
                  id={field}
                  placeholder={config.placeholder}
                  value={formData[field] || ''}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  className="resize-none min-h-[80px] bg-input border-input-border"
                  disabled={isLoading}
                />
              ) : (
                <Input
                  id={field}
                  type={config.type}
                  placeholder={config.placeholder}
                  value={formData[field] || ''}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  className="bg-input border-input-border"
                  disabled={isLoading}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-3 pt-4 border-t border-border/50">
        <Button
          onClick={handleProvideInfo}
          disabled={isLoading || Object.keys(formData).length === 0}
          className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            'Provide & Continue'
          )}
        </Button>
        
        <Button
          variant="outline"
          onClick={handleContinue}
          disabled={isLoading}
          className="flex-1 border-input-border hover:bg-accent"
        >
          Continue Without
        </Button>
      </div>

      <div className="text-xs text-muted-foreground text-center">
        Your information is secure and will only be used to optimize your resume
      </div>
    </Card>
  );
}
